import { useState, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
import FieldActions from './FieldActions';
import { FieldDataTypeEnum, FormField, FormFieldTest } from '../../../types/types';
import { CreateFormTheme } from '../../../utils/themes';
import { useTheme } from '../../../middleware/ThemeContext';
import StyledToolTip from '../../StyledComponents/StyledTooltip';
import TooltipIcon from '../../../assets/icons/Tooltip';
import DeleteIcon from '../../../assets/icons/DeleteIcon';
import { FormFieldControl } from './FormFieldControl';

interface FieldPreviewProps {
  formField: FormFieldTest;
  onEdit: () => void;
  onDelete: () => void;
  onCopy: () => void;
  onToggleRequired: (isRequired: boolean) => void;
  onSelectRow?: () => void;
  isLayoutField?: boolean;
  fieldName?: string;
}

const FieldPreview = ({ formField, onEdit, onDelete, onCopy, onToggleRequired, onSelectRow, isLayoutField, fieldName }: FieldPreviewProps) => {
  const { theme } = useTheme();
  const [selected, setSelected] = useState(false);
  const [hovered, setHovered] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setSelected(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Layout fields keep hover actions; other editable fields get a selected delete control.
  const showActions = !!isLayoutField && hovered;
  const showDeleteButton =
    selected &&
    !isLayoutField &&
    formField?.alignment !== 0 &&
    formField?.field?.dataType !== FieldDataTypeEnum.DISPLAY_ONLY;

  return (
    <PreviewContainer
      theme={theme}
      ref={wrapperRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={(e) => {
        e.stopPropagation();
        setSelected(true);
        if (onSelectRow) {
          onSelectRow();
        }
      }}
      $selected={formField?.alignment !== 0 && selected}
      $disabled={formField?.field?.config?.uiMeta?.disabled}
      $hidden={formField?.field?.config?.uiMeta?.hideField}
      $isSingle={formField?.alignment === 0}
      $isLayoutField={isLayoutField}
    >
      {showActions && <FieldActions onEdit={onEdit} onCopy={onCopy} onDelete={onDelete} fieldName={fieldName} />}
      {showDeleteButton && (
        <DeleteActionButton
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete();
          }}
          aria-label="Delete field"
          title="Delete field"
        >
          <DeleteIcon size={16} />
        </DeleteActionButton>
      )}
      <div
        className={`d-flex align-items-center w-100`}
        style={{ gap: "0.5rem" }} // optional spacing
      >
        <div className="flex-grow-1">
          <FormFieldControl
            formField={formField}
            value={''}
            onChange={() => { }}
            forPreview
            onToggleRequired={onToggleRequired}
          />
        </div>
        {formField?.field?.config?.uiMeta?.toolTipText && (
          <div className="flex-shrink-0">
            <StyledToolTip
              placement="top"
              title={formField?.field?.config?.uiMeta?.toolTipText || ''}
            >
              <TooltipIcon
                size={20}
                color={theme.CorporateTheme.colors.textSecondary}
              />
            </StyledToolTip>
          </div>
        )}
      </div>



    </PreviewContainer>
  );
};

export default FieldPreview;

const PreviewContainer = styled('div')<{
  $hidden?: boolean;
  $disabled?: boolean;
  $selected?: boolean;
  $isSingle?: boolean;
  $isLayoutField?: boolean;
}>(({ theme, $hidden, $disabled, $selected, $isSingle, $isLayoutField }) => ({
  width: '100%',
  padding: '4px 8px',
  position: 'relative',
  background: theme.CreateFormTheme.colors.addressBG,
  borderRadius: '4px',
  cursor: 'pointer',
  transition: 'opacity 0.3s ease',
  overflow: 'visible',
  border: `1px solid ${theme.CreateFormTheme.colors.addressBG}`,

  ...(!!$selected && {
    background: theme.CreateFormTheme.colors.addressBG,
    border: `1px solid ${theme.CreateFormTheme?.colors?.primary || '#0556cb'}`,
    borderTopLeftRadius: '0px',
    borderBottomLeftRadius: '0px',
  }),

  ...(!!!$isSingle && {
    '&:hover': {
      border: `1px solid ${theme.CreateFormTheme?.colors?.primary || '#0556cb'}`,
    },
  }),

  ...(!!$hidden && {
    opacity: 0.5,
    pointerEvents: 'none',
  }),

  ...(!!$disabled && {
    opacity: 0.7,
    cursor: 'not-allowed',
  }),
}));

const DeleteActionButton = styled.button`
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  z-index: 6;

  &:hover {
    background: #f5f5f5;
  }
`;


